import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface FullPageProps {
  children?: React.ReactNode;
  className?: string;
  fillViewport?: boolean; // Whether to fill the remaining viewport height
  stateHolder?: boolean; // Whether to center content as a state holder (for EmptyState/PreviewState)
  minHeight?: string | number; // Custom min-height when stateHolder is true (ignored when fillViewport is true)
}

export const FullPage: React.FC<FullPageProps> = ({
  children,
  className = '',
  fillViewport = true,
  stateHolder = false,
  minHeight
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Calculate initial height immediately to prevent layout shift
  const getInitialHeight = () => {
    if (typeof window === 'undefined') return '100vh';
    
    const primaryHeader = document.querySelector('.core-header');
    const primaryHeaderHeight = primaryHeader ? primaryHeader.getBoundingClientRect().height : 48;
    
    const fullHeader = document.querySelector('.full-header');
    const fullHeaderHeight = fullHeader ? fullHeader.getBoundingClientRect().height : 80;
    
    const fullTabs = document.querySelector('.full-tabs');
    const fullTabsHeight = fullTabs ? fullTabs.getBoundingClientRect().height : 64;
    
    const remainingHeight = window.innerHeight - primaryHeaderHeight - fullHeaderHeight - fullTabsHeight;
    
    return remainingHeight > 0 ? `${remainingHeight}px` : '100vh';
  };
  
  const [calculatedHeight, setCalculatedHeight] = useState<string>(() => getInitialHeight());

  useLayoutEffect(() => {
    if (!fillViewport || !containerRef.current) {
      return;
    }

    const calculateHeight = () => {
      const container = containerRef.current;
      if (!container) return;

      // Find PrimaryHeader (used in console/panel/director apps - 48px)
      const primaryHeader = document.querySelector('.core-header');
      const primaryHeaderHeight = primaryHeader ? primaryHeader.getBoundingClientRect().height : 0;

      // Find FullHeader (used in FullLayout - variable height)
      const fullHeader = document.querySelector('.full-header');
      const fullHeaderHeight = fullHeader ? fullHeader.getBoundingClientRect().height : 0;

      // Find FullTabs (used in FullLayout - 64px when present)
      const fullTabs = document.querySelector('.full-tabs');
      const fullTabsHeight = fullTabs ? fullTabs.getBoundingClientRect().height : 0;

      // Calculate remaining height: viewport - all headers - tabs
      const remainingHeight = window.innerHeight - primaryHeaderHeight - fullHeaderHeight - fullTabsHeight;
      
      // Set the height only if it's positive
      if (remainingHeight > 0) {
        setCalculatedHeight(`${remainingHeight}px`);
      }
    };

    // Force synchronous layout calculation before first paint
    // This ensures headers are measured even during fast page switches
    const primaryHeader = document.querySelector('.core-header');
    const fullHeader = document.querySelector('.full-header');
    const fullTabs = document.querySelector('.full-tabs');
    
    // Force browser to compute layout if elements exist
    if (primaryHeader) primaryHeader.getBoundingClientRect();
    if (fullHeader) fullHeader.getBoundingClientRect();
    if (fullTabs) fullTabs.getBoundingClientRect();
    
    // Now calculate with accurate measurements
    calculateHeight();

    // Recalculate on window resize
    window.addEventListener('resize', calculateHeight);

    // Observe all headers and tabs for size changes
    const resizeObserver = new ResizeObserver(() => {
      calculateHeight();
    });
    
    if (primaryHeader) resizeObserver.observe(primaryHeader);
    if (fullHeader) resizeObserver.observe(fullHeader);
    if (fullTabs) resizeObserver.observe(fullTabs);

    return () => {
      window.removeEventListener('resize', calculateHeight);
      resizeObserver.disconnect();
    };
  }, [fillViewport]);

  // Calculate content styles for state holder
  const contentStyle = React.useMemo(() => {
    if (stateHolder) {
      const baseStyle = {
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%'
      };
      
      // Add minHeight only if not filling viewport and minHeight is specified
      if (!fillViewport && minHeight) {
        return {
          ...baseStyle,
          minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight
        };
      }
      
      return baseStyle;
    }
    return undefined;
  }, [stateHolder, fillViewport, minHeight]);

  return (
    <div 
      ref={containerRef}
      className={`full-page ${fillViewport ? 'full-page--fill' : ''} ${stateHolder ? 'full-page--state-holder' : ''} ${className}`}
      style={fillViewport ? { height: calculatedHeight } : undefined}
    >
      <div 
        className="full-page-content"
        style={contentStyle}
      >
        {children}
      </div>
    </div>
  );
};
